"use client";

import { devLabel, devLabelPanel } from "@/lib/dev-labels";

export interface ToolDef {
	id: string;
	label: string;
	subLabel?: string;
	badge?: "MML+" | "AI" | "HD";
	iconBg?: string;
	devLabel: string;
}

const TOOLS: ToolDef[] = [
	{
		id: "long-to-shorts",
		label: "긴 영상 → Shorts",
		subLabel: "(자동 분할)",
		badge: "MML+",
		iconBg: "linear-gradient(135deg,#4eb3c1,#2a7a8a)",
		devLabel: "12-A",
	},
	{
		id: "ai-magic-wand",
		label: "AI Magic Wand",
		subLabel: "(50 wizards)",
		badge: "MML+",
		iconBg: "linear-gradient(135deg,#d4af37,#a07f23)",
		devLabel: "12-B",
	},
	{
		id: "ai-video",
		label: "AI 동영상",
		badge: "AI",
		devLabel: "12-C",
	},
	{
		id: "ai-image",
		label: "AI 이미지",
		badge: "AI",
		devLabel: "12-D",
	},
	{
		id: "translator",
		label: "동영상 번역기",
		badge: "AI",
		devLabel: "12-E",
	},
	{
		id: "tts-narration",
		label: "TTS 나레이션",
		subLabel: "(2-Mode)",
		badge: "MML+",
		iconBg: "linear-gradient(135deg,#a05a1a,#6a3a0a)",
		devLabel: "12-F",
	},
	{
		id: "hook-analysis",
		label: "후킹 분석",
		subLabel: "(Live)",
		badge: "MML+",
		iconBg: "linear-gradient(135deg,#22c55e,#0a7a3e)",
		devLabel: "12-G",
	},
	{
		id: "quality-fix",
		label: "품질 보정",
		badge: "HD",
		devLabel: "12-H",
	},
	{
		id: "batch",
		label: "멀티 batch",
		subLabel: "(10+ URL)",
		badge: "MML+",
		iconBg: "linear-gradient(135deg,#c9b3ff,#5a3b9c)",
		devLabel: "12-I",
	},
];

function ToolIcon({ id }: { id: string }) {
	const common = {
		width: 22,
		height: 22,
		viewBox: "0 0 24 24",
		fill: "none",
		stroke: "currentColor",
		strokeWidth: 2,
		strokeLinecap: "round" as const,
		strokeLinejoin: "round" as const,
	};
	switch (id) {
		case "long-to-shorts":
			return (
				<svg {...common} fill="currentColor" stroke="none">
					<path d="M5 3l14 9-14 9V3z" />
				</svg>
			);
		case "ai-magic-wand":
		case "hook-analysis":
			return (
				<svg {...common} fill="currentColor" stroke="none">
					<polygon points="12 2 15 9 22 9.3 17 14 18.5 21 12 17.3 5.5 21 7 14 2 9.3 9 9 12 2" />
				</svg>
			);
		case "ai-video":
			return (
				<svg {...common}>
					<polygon points="23 7 16 12 23 17 23 7" />
					<rect x="1" y="5" width="15" height="14" rx="2" />
				</svg>
			);
		case "ai-image":
			return (
				<svg {...common}>
					<circle cx="12" cy="12" r="10" />
					<circle cx="6.5" cy="11.5" r="1.5" fill="currentColor" />
					<circle cx="9.5" cy="7.5" r="1.5" fill="currentColor" />
					<circle cx="14.5" cy="7.5" r="1.5" fill="currentColor" />
					<circle cx="17.5" cy="11.5" r="1.5" fill="currentColor" />
					<path d="M12 22a10 10 0 0 0 0-20" />
				</svg>
			);
		case "translator":
			return (
				<svg {...common}>
					<circle cx="12" cy="12" r="10" />
					<line x1="2" y1="12" x2="22" y2="12" />
					<path d="M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z" />
				</svg>
			);
		case "tts-narration":
			return (
				<svg {...common}>
					<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
					<path d="M19 10v2a7 7 0 0 1-14 0v-2" />
					<line x1="12" y1="19" x2="12" y2="23" />
					<line x1="8" y1="23" x2="16" y2="23" />
				</svg>
			);
		case "quality-fix":
			return (
				<svg {...common} fill="currentColor" stroke="none">
					<polygon points="12 2 13.5 8.5 19 10 13.5 11.5 12 17 10.5 11.5 5 10 10.5 8.5 12 2" />
				</svg>
			);
		case "batch":
			return (
				<svg {...common}>
					<line x1="3" y1="6" x2="21" y2="6" />
					<line x1="3" y1="12" x2="21" y2="12" />
					<line x1="3" y1="18" x2="21" y2="18" />
				</svg>
			);
		default:
			return null;
	}
}

export interface ToolsCardGridProps {
	onToolClick?: (toolId: string) => void;
}

export function ToolsCardGrid({ onToolClick }: ToolsCardGridProps) {
	return (
		<section style={{ marginBottom: 24 }} {...devLabelPanel("12 · Tools")}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: 10,
				}}
			>
				<div style={{ fontSize: 14, fontWeight: 600 }}>추가 도구</div>
				<span
					style={{
						fontSize: 11,
						color: "#a3a3a3",
						display: "inline-flex",
						alignItems: "center",
						gap: 3,
						cursor: "pointer",
					}}
				>
					더 보기
					<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
						<polyline points="6 9 12 15 18 9" />
					</svg>
				</span>
			</div>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(9,1fr)",
					gap: 8,
				}}
			>
				{TOOLS.map((t) => (
					<button
						key={t.id}
						type="button"
						onClick={() => onToolClick?.(t.id)}
						{...devLabel(t.devLabel)}
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							padding: "14px 6px",
							borderRadius: 8,
							cursor: "pointer",
							background: "#1a1a1a",
							border: "1px solid rgba(255,255,255,0.05)",
							transition: "all 0.15s",
							position: "relative",
							color: "#dcdcdc",
						}}
					>
						<div
							style={{
								width: 48,
								height: 48,
								borderRadius: 8,
								background:
									t.iconBg ?? "linear-gradient(135deg,#3a3a3a,#1a1a1a)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: 22,
								marginBottom: 6,
								position: "relative",
								color: t.iconBg ? "#0a0a0a" : "#dcdcdc",
							}}
						>
							<ToolIcon id={t.id} />
							{t.badge && (
								<span
									style={{
										position: "absolute",
										top: -3,
										right: -3,
										padding: "0 4px",
										fontSize: 8,
										fontWeight: 700,
										background:
											t.badge === "MML+"
												? "#d4af37"
												: t.badge === "AI"
													? "#4eb3c1"
													: "#fff",
										color: "#000",
										borderRadius: 3,
										letterSpacing: "0.05em",
									}}
								>
									{t.badge}
								</span>
							)}
						</div>
						<div
							style={{
								fontSize: 10,
								textAlign: "center",
								lineHeight: 1.3,
								color: "#dcdcdc",
							}}
						>
							{t.label}
							{t.subLabel && (
								<>
									<br />
									{t.subLabel}
								</>
							)}
						</div>
					</button>
				))}
			</div>
		</section>
	);
}
